import React, { useEffect, useMemo, useState } from "react";
import { FaSearch } from "react-icons/fa";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";

const normalizeAdmissionRecord = (record = {}, index = 0) => ({
  id: record.id || record.admissionId || record.formId || `ADM-${index + 1}`,
  studentName: record.studentName || record.name || record.fullName || "-",
  standard: record.standard || record.className || record.class || "-",
  guardian: record.guardianName || record.parentName || record.fatherName || "-",
  submittedOn: record.submittedOn || record.createdAt || record.applicationDate || "-",
  status: record.status || "Pending",
  feeStatus: record.feeStatus || record.paymentStatus || "Pending",
});

const extractAdmissionRecords = (responseData) => {
  const payload = responseData?.data ?? responseData ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.applications)) return payload.applications;
  if (Array.isArray(payload?.admissions)) return payload.admissions;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

const toneForStatus = (status) => {
  if (status === "Approved") return "bg-emerald-100 text-emerald-700";
  if (status === "Under Review") return "bg-amber-100 text-amber-700";
  return "bg-rose-100 text-rose-700";
};

export default function AdmissionDetails({ userRole, embedded = false }) {
  const [activeView, setActiveView] = useState("records");
  const [searchTerm, setSearchTerm] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecords = async () => {
      setLoading(true);
      try {
        const response = await api.get(API_PATHS.admission.adminDashboard);
        const nextRecords = extractAdmissionRecords(response.data).map(normalizeAdmissionRecord);
        setRecords(nextRecords);
      } catch {
        setRecords([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, []);

  const filteredRecords = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return records.filter((item) => (
      !query
      || item.studentName.toLowerCase().includes(query)
      || item.id.toLowerCase().includes(query)
      || item.standard.toLowerCase().includes(query)
      || item.guardian.toLowerCase().includes(query)
      || item.status.toLowerCase().includes(query)
    ));
  }, [records, searchTerm]);

  const statusSummary = useMemo(() => ({
    total: records.length,
    approved: records.filter((item) => item.status === "Approved").length,
    review: records.filter((item) => item.status === "Under Review").length,
    pendingDocs: records.filter((item) => item.status === "Documents Pending").length,
  }), [records]);

  return (
    <div className={embedded ? "" : "min-h-screen bg-slate-50 px-4 py-8"}>
      <div className={embedded ? "space-y-4" : "mx-auto max-w-7xl space-y-4"}>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-6">
          {[
            { key: "records", label: "Records" },
            { key: "summary", label: "Summary" },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveView(tab.key)}
              className={`rounded-t-md border px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                activeView === tab.key
                  ? "border-amber-300 bg-amber-50 text-amber-700 shadow-[0_0_0_1px_rgba(245,158,11,0.22)]"
                  : "border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="border-x border-b border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/60">
          {activeView === "records" && (
            <>
              <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Student records</p>
                  <h2 className="mt-1 text-2xl font-bold text-slate-900">Admissions and student submissions</h2>
                </div>
                <div className="relative w-full md:w-96">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="w-full rounded-2xl border border-slate-300 py-2.5 pl-10 pr-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100"
                    placeholder="Search name, id, class, guardian"
                  />
                </div>
              </div>

              <div className="overflow-hidden rounded-2xl border border-slate-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-100 text-slate-600">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Admission ID</th>
                        <th className="px-4 py-3 text-left font-semibold">Student</th>
                        <th className="px-4 py-3 text-left font-semibold">Class</th>
                        <th className="px-4 py-3 text-left font-semibold">Guardian</th>
                        <th className="px-4 py-3 text-left font-semibold">Submitted</th>
                        <th className="px-4 py-3 text-left font-semibold">Status</th>
                        <th className="px-4 py-3 text-left font-semibold">Fee</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white text-slate-800">
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="px-4 py-10 text-center text-sm text-slate-500">Loading records...</td>
                        </tr>
                      ) : filteredRecords.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-4 py-10 text-center text-sm text-slate-500">No student records found.</td>
                        </tr>
                      ) : filteredRecords.map((item) => (
                        <tr key={item.id} className="border-t border-slate-200 hover:bg-slate-50">
                          <td className="px-4 py-3 font-semibold">{item.id}</td>
                          <td className="px-4 py-3">{item.studentName}</td>
                          <td className="px-4 py-3">{item.standard}</td>
                          <td className="px-4 py-3">{item.guardian}</td>
                          <td className="px-4 py-3">{item.submittedOn}</td>
                          <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${toneForStatus(item.status)}`}>{item.status}</span></td>
                          <td className="px-4 py-3">{item.feeStatus}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {activeView === "summary" && (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Summary</p>
                <h2 className="mt-1 text-2xl font-bold text-slate-900">Admissions snapshot</h2>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {[
                  { label: "Total records", value: statusSummary.total },
                  { label: "Approved", value: statusSummary.approved },
                  { label: "Under review", value: statusSummary.review },
                  { label: "Docs pending", value: statusSummary.pendingDocs },
                ].map((item) => (
                  <div key={item.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{item.label}</p>
                    <p className="mt-2 text-2xl font-bold text-slate-900">{item.value}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                <p className="font-semibold text-slate-800">Role context</p>
                <p className="mt-1">Current role: {userRole || "Management"}. This view can be wired to show role-specific records from backend.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
